/**
 * Curated countries & cities for admin exhibition forms (no external API — works offline).
 * Values are stored as-is in the exhibitions table.
 */

/** Common exhibition destinations + ISO-style country names (alphabetical). */
export const EXHIBITION_COUNTRIES = [
  'Australia',
  'Austria',
  'Bahrain',
  'Belgium',
  'Canada',
  'China',
  'France',
  'Germany',
  'Hong Kong',
  'India',
  'Indonesia',
  'Italy',
  'Japan',
  'Kenya',
  'Kuwait',
  'Malaysia',
  'Nepal',
  'Netherlands',
  'Oman',
  'Qatar',
  'Saudi Arabia',
  'Singapore',
  'South Africa',
  'South Korea',
  'Spain',
  'Sri Lanka',
  'Switzerland',
  'Thailand',
  'Turkey',
  'UAE',
  'United Kingdom',
  'United States',
  'Vietnam',
];

/** Stored country label → name expected by countriesnow.space API. */
export const EXHIBITION_COUNTRY_API_NAMES = {
  UAE: 'United Arab Emirates',
};

/** Resolve the API lookup name for a stored exhibition country. */
export function getExhibitionCountryApiName(country) {
  const canonical = canonicalExhibitionCountry(country);
  if (!canonical) return '';
  return EXHIBITION_COUNTRY_API_NAMES[canonical] || canonical;
}

/** Suggested cities per country (admin can still type a custom city). */
export const EXHIBITION_CITIES_BY_COUNTRY = {
  India: [
    'Ahmedabad',
    'Bengaluru',
    'Bhopal',
    'Chandigarh',
    'Chennai',
    'Coimbatore',
    'Delhi',
    'Hyderabad',
    'Indore',
    'Jaipur',
    'Kochi',
    'Kolkata',
    'Lucknow',
    'Mumbai',
    'Nagpur',
    'New Delhi',
    'Pune',
    'Raipur',
    'Surat',
    'Vadodara',
  ],
  Singapore: ['Singapore'],
  'United Arab Emirates': ['Abu Dhabi', 'Dubai', 'Sharjah'],
  UAE: ['Abu Dhabi', 'Dubai', 'Sharjah'],
  'United States': [
    'Chicago',
    'Dallas',
    'Houston',
    'Las Vegas',
    'Los Angeles',
    'Miami',
    'New York',
    'San Francisco',
  ],
  'United Kingdom': ['Birmingham', 'Edinburgh', 'Glasgow', 'London', 'Manchester'],
  Australia: ['Brisbane', 'Melbourne', 'Perth', 'Sydney'],
  Canada: ['Calgary', 'Montreal', 'Toronto', 'Vancouver'],
  Germany: ['Berlin', 'Cologne', 'Frankfurt', 'Munich'],
  France: ['Lyon', 'Marseille', 'Nice', 'Paris'],
  Italy: ['Milan', 'Rome', 'Venice'],
  China: ['Beijing', 'Guangzhou', 'Hong Kong', 'Shanghai', 'Shenzhen'],
  'Hong Kong': ['Hong Kong'],
  Japan: ['Kyoto', 'Osaka', 'Tokyo'],
  'South Korea': ['Busan', 'Seoul'],
  Thailand: ['Bangkok', 'Chiang Mai'],
  Malaysia: ['Johor Bahru', 'Kuala Lumpur', 'Penang'],
  Indonesia: ['Bali', 'Jakarta', 'Surabaya'],
  'Saudi Arabia': ['Jeddah', 'Riyadh'],
  Qatar: ['Doha'],
  Kuwait: ['Kuwait City'],
  Bahrain: ['Manama'],
  Oman: ['Muscat'],
  'South Africa': ['Cape Town', 'Durban', 'Johannesburg'],
  'Sri Lanka': ['Colombo'],
  Nepal: ['Kathmandu'],
  Switzerland: ['Basel', 'Geneva', 'Zurich'],
  Spain: ['Barcelona', 'Madrid'],
  Turkey: ['Istanbul'],
  Vietnam: ['Hanoi', 'Ho Chi Minh City'],
  Kenya: ['Nairobi'],
  Austria: ['Vienna'],
  Belgium: ['Antwerp', 'Brussels'],
  Netherlands: ['Amsterdam', 'Rotterdam'],
};

const COUNTRY_CANONICAL = {
  uae: 'UAE',
  'u.a.e.': 'UAE',
  'united arab emirates': 'UAE',
  uk: 'United Kingdom',
  'u.k.': 'United Kingdom',
  'great britain': 'United Kingdom',
  usa: 'United States',
  'u.s.a.': 'United States',
  'u.s.': 'United States',
  'united states of america': 'United States',
  singapore: 'Singapore',
  india: 'India',
};

export function normalizeLocationKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

/** Map legacy / shorthand country labels to a canonical pick-list value when possible. */
export function canonicalExhibitionCountry(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  const key = normalizeLocationKey(trimmed);
  if (COUNTRY_CANONICAL[key]) return COUNTRY_CANONICAL[key];
  const exact = EXHIBITION_COUNTRIES.find((c) => normalizeLocationKey(c) === key);
  return exact || trimmed;
}

const CITY_CANONICAL = {
  bengaluru: 'Bengaluru',
  banglore: 'Bengaluru',
  bangalore: 'Bengaluru',
  sinapore: 'Singapore',
  puune: 'Pune',
  'new delhi': 'New Delhi',
  mumbai: 'Mumbai',
  dubai: 'Dubai',
};

/** Normalize common city typos on save (country-aware where needed). */
export function canonicalExhibitionCity(value, country = '') {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  const key = normalizeLocationKey(trimmed);
  if (CITY_CANONICAL[key]) return CITY_CANONICAL[key];
  if (normalizeLocationKey(country) === 'singapore' && key === 'sinapore') return 'Singapore';
  return trimmed;
}

/** Country dropdown options: curated list + any values already in the database. */
export function buildExhibitionCountryOptions(knownFromDb = []) {
  const seen = new Map();
  for (const name of EXHIBITION_COUNTRIES) {
    seen.set(normalizeLocationKey(name), name);
  }
  for (const raw of knownFromDb) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) continue;
    const canonical = canonicalExhibitionCountry(trimmed);
    const key = normalizeLocationKey(canonical);
    if (!seen.has(key)) {
      seen.set(key, canonical);
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/** Merge city name lists (deduped, case-insensitive). */
export function mergeExhibitionCityLists(...lists) {
  const seen = new Map();
  for (const list of lists) {
    for (const raw of list || []) {
      const trimmed = String(raw || '').trim();
      if (trimmed) seen.set(normalizeLocationKey(trimmed), trimmed);
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/** City suggestions: curated for country + cities already saved for that country. */
export function buildExhibitionCityOptions(country, knownFromDb = [], apiCities = []) {
  const countryKey = normalizeLocationKey(canonicalExhibitionCountry(country));
  const seen = new Map();

  for (const [countryName, cities] of Object.entries(EXHIBITION_CITIES_BY_COUNTRY)) {
    if (normalizeLocationKey(countryName) !== countryKey) continue;
    for (const city of cities) {
      const trimmed = String(city || '').trim();
      if (trimmed) seen.set(normalizeLocationKey(trimmed), trimmed);
    }
  }

  for (const raw of knownFromDb) {
    const trimmed = String(raw || '').trim();
    if (trimmed) seen.set(normalizeLocationKey(trimmed), trimmed);
  }

  for (const raw of apiCities) {
    const trimmed = String(raw || '').trim();
    if (trimmed) seen.set(normalizeLocationKey(trimmed), trimmed);
  }

  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

const DATALIST_MATCH_LIMIT = 150;

/** Filter options for datalist while typing (prefix matches first). */
export function filterOptionsForDatalist(options, query, limit = DATALIST_MATCH_LIMIT) {
  const q = normalizeLocationKey(query);
  if (!q) {
    return (options || [])
      .map((item) => String(item || '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }

  const startsWith = [];
  const contains = [];
  for (const item of options || []) {
    const trimmed = String(item || '').trim();
    if (!trimmed) continue;
    const key = normalizeLocationKey(trimmed);
    if (key.startsWith(q)) startsWith.push(trimmed);
    else if (key.includes(q)) contains.push(trimmed);
  }

  startsWith.sort((a, b) => a.localeCompare(b));
  contains.sort((a, b) => a.localeCompare(b));
  return [...startsWith, ...contains].slice(0, limit);
}

/** @deprecated Use filterOptionsForDatalist */
export function filterCitiesForDatalist(cities, query, limit = DATALIST_MATCH_LIMIT) {
  return filterOptionsForDatalist(cities, query, limit);
}

/** Pick the list option that matches typed text (case-insensitive). */
export function matchLocationOption(value, options) {
  const key = normalizeLocationKey(value);
  if (!key) return '';
  for (const name of options || []) {
    if (normalizeLocationKey(name) === key) return name;
  }
  return '';
}

/** Whether we can load the full city list for this country from the API. */
export function isQueryableExhibitionCountry(country) {
  const canonical = canonicalExhibitionCountry(country);
  if (!canonical) return false;
  const key = normalizeLocationKey(canonical);
  return EXHIBITION_COUNTRIES.some((name) => normalizeLocationKey(name) === key);
}

/** Unique non-empty countries from exhibition rows. */
export function uniqueCountriesFromRows(rows) {
  return buildExhibitionCountryOptions(
    (rows || []).map((row) => row?.country).filter(Boolean),
  );
}

/** Cities used in DB for a given country (for datalist merge). */
export function citiesFromRowsForCountry(rows, country) {
  const target = normalizeLocationKey(canonicalExhibitionCountry(country));
  if (!target) return [];
  const out = [];
  for (const row of rows || []) {
    const rowCountry = normalizeLocationKey(canonicalExhibitionCountry(row?.country));
    const city = String(row?.city || '').trim();
    if (rowCountry === target && city) out.push(city);
  }
  return out;
}
